import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import UltraLuxLogo from 'LuxKit/Label/UltraLuxLogo'
import TabButton from 'LuxKit/Tabs/TabButton'
import TabButtonsGroup from 'LuxKit/Tabs/TabButtonsGroup'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import HotelSearchPageFilters from 'components/Pages/HotelSearchPage/HotelSearchPageFilters'
import OfferList from 'components/OfferList/OfferList'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import useOffer from 'hooks/Offers/useOffer'
import useOfferList from 'hooks/Offers/useOfferList'
import { SEARCH_STREAMING_PAGE_SIZE } from 'search/getSearchSocket'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import switchFunc from 'lib/function/switchFunc'
import { scrollToId } from 'lib/window/scrollUtils'
import { rem } from 'polished'
import { useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import * as React from 'react'
import { defineMessages, type IntlShape, useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import styled from 'styled-components'
import UltraLuxVerticalPageUSPs from './UltraLuxVerticalPageUSPs'
import zIndex from 'styles/tools/z-index'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import { InView } from 'react-intersection-observer'
import { useLocation } from 'react-router-dom'
import debounce from 'debounce-promise'
import LineShipIcon from 'LuxKit/Icons/line/LineShipIcon'
import GeoContext from 'contexts/geoContext'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'

import { SearchBurnProvider } from 'contexts/SearchPointsContext'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import { getListName } from 'analytics/enhanced-ecommerce'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { ANYWHERE_PLACE_ID, ANYWHERE_NAME } from 'constants/search'
import getOfferListKey from 'lib/offer/offerListKey'
import type { FilterControlChangeHandler } from 'components/Pages/HotelSearchPage/HotelSearchPageFilterPane'
import { replace } from 'lib/routing/appRouting'
import { useAppDispatch } from 'hooks/reduxHooks'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'

const videoUrl = 'https://player.vimeo.com/progressive_redirect/playback/782405070/rendition/1080p/file.mp4?loc=external&signature=d2871cdd822bba9a89ad7436fe54dd2d7f3defa73b336eec2ef3d4afe4ebf091'

const UltraluxVertical = styled.div`
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const OffersSection = styled(VerticalSpacer)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(32)};
`

const HeroContent = styled(LayoutContainer)`
  display: flex;
  height: 100%;
  width: 100%;
  color: ${props => props.theme.palette.neutral.surface.darkContrast};
  position: absolute;
  inset: 0;
  align-items: center;
  justify-content: center;
`
const UltraluxImageBanner = styled.div`
  height: ${rem(350)};
  position: relative;

  ${mediaQueryUp.tablet} {
    height: ${rem(400)};
  }
`

const VerticalSelectorWrapper = styled.div`
  position: sticky;
  top: 0;
  margin-inline: ${rem(-12)};
  z-index: ${zIndex.stickyHeader};

  ${mediaQueryUp.tablet} {
    position: absolute;
    width: max-content;
    max-width: 100%;
    top: ${rem(20)};
    margin: 0;
    padding: 0 ${rem(12)};
    display: flex;
    justify-content: center;
    /* oxlint-disable-next-line logical-css/no-physical-properties */
    left: 50%;
    transform: translateY(-100%) translateX(-50%);
    transition: transform 0.3s, top 0.3s;
    z-index: 1;

    &.full-size {
      position: fixed;
      top: 0;
      transform: translateY(0) translateX(-50%);
      z-index: ${zIndex.stickyHeader};
    }
  }
`

const VerticalSelector = styled(Pane)`
  position: absolute;
  inset: 0;

  ${mediaQueryUp.tablet} {
    box-shadow: ${props => props.theme.shadow.bottom.medium};
    border-radius: 2px;
    transform: scaleX(1);
    transition: transform 0.3s;

    &.full-size {
      transition: transform 0.3s;
      transform: scaleX(var(--menu-scale, 1));
    }
  }
`

const UltraluxHeroVideo = styled.video`
  height: 100%;
  width: 100%;
  object-fit: cover;
`

interface Props {
  filters: App.OfferListFilters
}

type UltraLuxVerticals = 'hotels' | 'tours' | 'cruises'

interface VerticalData {
  id: UltraLuxVerticals
  offerTypes: Array<App.OfferListOfferType>
  filters: App.OfferListFilters
  filtersUnifiedEndpoint: App.OfferListFilters
  verticalPageUrl: string
}

const hotelConfig: VerticalData = {
  id: 'hotels',
  offerTypes: ['hotel', 'tactical_ao_hotel'],
  filters: { propertySubCategories: ['Ultra Lux'] },
  filtersUnifiedEndpoint: {},
  verticalPageUrl: '/hotels',
}

const tourConfig: VerticalData = {
  id: 'tours',
  offerTypes: ['tour_v2'],
  filters: { productTypes: ['ultra_lux_tour'] },
  filtersUnifiedEndpoint: {},
  verticalPageUrl: '/tours',
}

const cruiseConfig: VerticalData = {
  id: 'cruises',
  offerTypes: ['tour'],
  filters: { productTypes: ['ultra_lux_tour'], holidayTypes: ['Cruises'] },
  filtersUnifiedEndpoint: { holidayTypes: ['Cruises'] },
  verticalPageUrl: '/cruises',
}

const verticals = switchFunc<VerticalData, string>({
  hotels: hotelConfig,
  tours: tourConfig,
  cruises: cruiseConfig,
},
// default back to hotel
hotelConfig)

const verticalMessages = defineMessages({
  hotelsListingHeading: {
    id: '793FYeiJl4RpFkJZXHXF',
    defaultMessage: 'Explore our <i>ultra lux</i> hotels',
    description: 'Heading above the Ultra Lux hotel offer list on the Ultra Lux vertical page',
  },
  toursListingHeading: {
    id: '1wAE_cdTmh20AmS5U-eq',
    defaultMessage: 'Explore our <i>ultra lux</i> tours',
    description: 'Heading above the Ultra Lux tour offer list on the Ultra Lux vertical page',
  },
  cruisesListingHeading: {
    id: '07Q58X5KJgz0VwS1Kgb6',
    defaultMessage: 'Explore our <i>ultra lux</i> cruises',
    description: 'Heading above the Ultra Lux cruise offer list on the Ultra Lux vertical page',
  },
  hotelsNoResultsHeading: {
    id: 'X6iYxo5Oa9_YQTU-nBf4',
    defaultMessage: 'Unfortunately, there are no Ultra Lux hotels available at this time.',
    description: 'No-results heading when Ultra Lux hotel offers are unavailable on the vertical page',
  },
  toursNoResultsHeading: {
    id: 'aVKZslgIUev3Pg8Cv5XR',
    defaultMessage: 'Unfortunately, there are no Ultra Lux tours available at this time.',
    description: 'No-results heading when Ultra Lux tour offers are unavailable on the vertical page',
  },
  cruisesNoResultsHeading: {
    id: 'lncROUYNMDfccbg4bsjA',
    defaultMessage: 'Unfortunately, there are no Ultra Lux cruises available at this time.',
    description: 'No-results heading when Ultra Lux cruise offers are unavailable on the vertical page',
  },
  hotelsNoResultsBody: {
    id: '9GeVRfW51TC_ujKWIyWu',
    defaultMessage: 'Check out our other hotels collection for more options or call our customer service team 24 hours, 7 days for personalised recommendations.',
    description: 'No-results body directing customers to hotels or support on the Ultra Lux vertical page',
  },
  toursNoResultsBody: {
    id: '6m1GTRZL_E0KH_HwGJcB',
    defaultMessage: 'Check out our other tours collection for more options or call our customer service team 24 hours, 7 days for personalised recommendations.',
    description: 'No-results body directing customers to tours or support on the Ultra Lux vertical page',
  },
  cruisesNoResultsBody: {
    id: 'UBlg0oatrRIs2GJGVGlj',
    defaultMessage: 'Check out our other cruises collection for more options or call our customer service team 24 hours, 7 days for personalised recommendations.',
    description: 'No-results body directing customers to cruises or support on the Ultra Lux vertical page',
  },
  hotelsViewMoreOptions: {
    id: 'Yet7QdETLAaFvkJrp0Th',
    defaultMessage: 'View more hotel options',
    description: 'Button that navigates to additional hotel options from the Ultra Lux no-results state',
  },
  toursViewMoreOptions: {
    id: 'tjRxhRfzXUXmtijo_nW1',
    defaultMessage: 'View more tour options',
    description: 'Button that navigates to additional tour options from the Ultra Lux no-results state',
  },
  cruisesViewMoreOptions: {
    id: 'AstOvPvKlXt9ZLD62Xdi',
    defaultMessage: 'View more cruise options',
    description: 'Button that navigates to additional cruise options from the Ultra Lux no-results state',
  },
})

interface LocalizedVerticalContent {
  listingHeading: React.ReactNode
  noResultsHeading: string
  noResultsBody: string
  viewMoreOptions: string
}

function formatItalic(chunks: React.ReactNode) {
  return <i key="italic">{chunks}</i>
}

function getLocalizedVerticalContent(intl: IntlShape, vertical: UltraLuxVerticals): LocalizedVerticalContent {
  switch (vertical) {
    case 'hotels':
      return {
        listingHeading: intl.formatMessage(verticalMessages.hotelsListingHeading, { i: formatItalic }),
        noResultsHeading: intl.formatMessage(verticalMessages.hotelsNoResultsHeading),
        noResultsBody: intl.formatMessage(verticalMessages.hotelsNoResultsBody),
        viewMoreOptions: intl.formatMessage(verticalMessages.hotelsViewMoreOptions),
      }
    case 'tours':
      return {
        listingHeading: intl.formatMessage(verticalMessages.toursListingHeading, { i: formatItalic }),
        noResultsHeading: intl.formatMessage(verticalMessages.toursNoResultsHeading),
        noResultsBody: intl.formatMessage(verticalMessages.toursNoResultsBody),
        viewMoreOptions: intl.formatMessage(verticalMessages.toursViewMoreOptions),
      }
    case 'cruises':
      return {
        listingHeading: intl.formatMessage(verticalMessages.cruisesListingHeading, { i: formatItalic }),
        noResultsHeading: intl.formatMessage(verticalMessages.cruisesNoResultsHeading),
        noResultsBody: intl.formatMessage(verticalMessages.cruisesNoResultsBody),
        viewMoreOptions: intl.formatMessage(verticalMessages.cruisesViewMoreOptions),
      }
  }
}

function UltraLuxVerticalPage(props: Props) {
  const { filters } = props
  const intl = useIntl()
  const { frontPageSortByTypes } = useOfferListSortOptions()
  const [showFullSizeBar, setShowFullSizeBar] = useState<boolean>()
  const { currentRegionCode } = useContext(GeoContext)
  const loc = useLocation()
  const hash = loc.hash.replace('#', '')
  const vertical = verticals(hash)
  const localizedVerticalContent = useMemo(
    () => getLocalizedVerticalContent(intl, vertical.id),
    [intl, vertical.id],
  )

  const isManualSortingEnabled = isManualSortingEnabledFor('ultralux')

  const recommendedFilters = useMemo((): App.OfferListFilters => ({
    ...filters,
    ...vertical.filtersUnifiedEndpoint,
    offerTypes: vertical.offerTypes,
    sortBy: 'vertical-ultralux',
    holidayTypesScoped: ['Ultra Lux'],
    limit: 250,
    destinationId: ANYWHERE_PLACE_ID,
  }), [filters, vertical.filtersUnifiedEndpoint, vertical.offerTypes])

  const finalFilters = useMemo((): App.OfferListFilters => {
    // Unified endpoint is a shallow endpoint and doesn't support sortBy filter, it's neccessary to use search endpoint instead.
    if (isManualSortingEnabled && !filters.sortBy) {
      return recommendedFilters
    }

    return {
      ...filters,
      ...vertical.filters,
      offerTypes: vertical.offerTypes,
      frontPageVisibleOnly: true,
      sortBy: filters.sortBy ?? 'homepage',
      destinationId: ANYWHERE_PLACE_ID,
    }
  }, [filters, isManualSortingEnabled, recommendedFilters, vertical.filters, vertical.offerTypes])

  // Keep the Recommended facets when the result order changes.
  const availableFilters = useOfferListFilters(isManualSortingEnabled ? recommendedFilters : finalFilters)

  // Stream sorted hotel results without changing the filter panel's existing facets.
  const offerListFilters = useMemo((): App.OfferListFilters => {
    if (vertical.id === 'hotels' && filters.sortBy) {
      return { ...finalFilters, isStream: true }
    }
    return finalFilters
  }, [filters.sortBy, finalFilters, vertical.id])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'ULTRALUX', 'Ultralux Page Offer List'),
    { key: getOfferListKey(offerListFilters) },
  )

  const [page, setPage] = useState(1)
  const minRequiredOffers = page * SEARCH_STREAMING_PAGE_SIZE
  const offerList = useOfferList(offerListFilters, { minRequiredOffers })
  const [firstOffer] = useOffer<App.Offer>(offerList.offerIds[0])
  const menuRef = useRef<HTMLDivElement | null>(null)
  const verticalSelectorRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState<number>()

  const dispatch = useAppDispatch()

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
      hash: params.hash,
    }))
  }, [dispatch])

  const scrollToOffers = useCallback(() => {
    const menuHeight = verticalSelectorRef.current?.offsetHeight ?? 0
    scrollToId('offers', -menuHeight)
  }, [])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(replace({
      search: params.search,
    }))
    scrollToOffers()
  }, [dispatch, scrollToOffers])

  const mapViewURL = `/search/map?adults=2&children=none&destinationId=${ANYWHERE_PLACE_ID}&destinationName=${ANYWHERE_NAME}`

  const switchView = {
    label: intl.formatMessage({
      id: 'XOgh0sNB2uFYVbuF1Dyp',
      defaultMessage: 'Map view',
      description: 'Label for map view toggle on Ultra Lux page on the map view',
    }),
    view: 'map' as const,
    URL: mapViewURL,
  }

  useEffect(() => {
    const calcScale = () => {
      setScale(window.innerWidth / (menuRef.current?.offsetWidth ?? 1))
    }
    const debouncedCalc = debounce(calcScale, 50)
    window.addEventListener('resize', debouncedCalc)
    calcScale()

    return () => window.removeEventListener('resize', debouncedCalc)
  }, [])

  const cruisesEnabled = useMemo(() => isEnabledForFeature(config.CRUISES_REGIONS, currentRegionCode), [currentRegionCode])

  const heroHeadingText = cruisesEnabled
    ? intl.formatMessage({
        id: '6E2shNohitnWjp1QaUno',
        defaultMessage: 'Experience the world’s most luxurious hotels, tours & cruises',
        description: 'Ultra Lux hero heading when cruises are enabled on the offer types landing page',
      })
    : intl.formatMessage({
        id: 'HRZDW4zOH1Gj1Y6T5ibp',
        defaultMessage: 'Experience the world’s most luxurious hotels & tours',
        description: 'Ultra Lux hero heading when cruises are disabled on the offer types landing page',
      })

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage}>
    <UltraluxVertical>
      <HeadData
        title={intl.formatMessage({
          id: '4O5Hask05F97yPwdQXAQ',
          defaultMessage: 'Ultra Lux by Luxury Escapes',
          description: 'Page title for the Ultra Lux vertical page on the offer types landing page',
        })}
        description={intl.formatMessage({
          id: 'BCqW_RxOgzlZ-amNW0FW',
          defaultMessage: 'Experience the world’s most luxurious stays. Exceptional 5 & 6 star properties. Browse all offers now.',
          description: 'Meta description for Ultra Lux vertical page for the experience',
        })}
        imageId={firstOffer?.image?.id}
        imageAlt={firstOffer?.image?.title}
      />
      <ErrorBoundary fallback={null}>

      </ErrorBoundary>
      <UltraluxImageBanner>
        <UltraluxHeroVideo
          controls={false}
          autoPlay
          playsInline
          loop
          muted
        >
          <source
            src={videoUrl}
            type="video/mp4"
          />
        </UltraluxHeroVideo>
        <HeroContent>
          <Group direction="vertical" horizontalAlign="center" gap={24}>
            <UltraLuxLogo height={28} />
            <Heading variant="heading3alt" colour="dark-contrast">
              {heroHeadingText}
            </Heading>
          </Group>
        </HeroContent>
      </UltraluxImageBanner>
      <LayoutContainer relative>
        <VerticalSelectorWrapper ref={verticalSelectorRef} className={showFullSizeBar ? 'full-size' : undefined}>
          <VerticalSelector style={{ '--menu-scale': scale }} ref={menuRef} className={showFullSizeBar ? 'full-size' : undefined} />
          <TabButtonsGroup>
            <TabButton
              size="medium"
              isActive={vertical.id === 'hotels'}
              href="#hotels"
              startIcon={<LineBedIcon />}
            >
              {intl.formatMessage({
                id: 'OM2BV4EaqgAYx4SVEI3E',
                defaultMessage: 'Ultra Lux Hotels',
                description: 'Tab label for Ultra Lux Hotels vertical on the hotel or room booking page',
              })}
            </TabButton>
            <TabButton
              size="medium"
              isActive={vertical.id === 'tours'}
              href="#tours"
              startIcon={<LineCompassIcon />}
            >
              {intl.formatMessage({
                id: 'RKSSmJtkW8VG7NDYzILA',
                defaultMessage: 'Ultra Lux Tours',
                description: 'Tab label for Ultra Lux Tours vertical on the tour offer detail page',
              })}
            </TabButton>
            {cruisesEnabled && <TabButton
              size="medium"
              isActive={vertical.id === 'cruises'}
              startIcon={<LineShipIcon />}
              href="#cruises"
            >
              {intl.formatMessage({
                id: '2ds6qkiitZc8rUyeYu41',
                defaultMessage: 'Ultra Lux Cruises',
                description: 'Tab label for Ultra Lux Cruises vertical in the cruise section',
              })}
            </TabButton>}
          </TabButtonsGroup>
        </VerticalSelectorWrapper>
        <InView rootMargin="20px" onChange={(inView: boolean) => setShowFullSizeBar(!inView)} />
        <UltraLuxVerticalPageUSPs verticalType={vertical.id} />
        <OffersSection id="offers" gap={40}>
          <SearchBurnProvider>
            <div>
              {vertical.id !== 'hotels' && <Heading variant="heading2" align="start">
                {localizedVerticalContent.listingHeading}
              </Heading>}
              {vertical.id === 'hotels' && <HotelSearchPageFilters
                heading={localizedVerticalContent.listingHeading}
                filters={finalFilters}
                availableFilters={availableFilters}
                sortOptions={frontPageSortByTypes}
                switchView={switchView}
                shouldShowFilters
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                doNotScrollToTopOfSelection
                onViewOffers={scrollToOffers}
              />}
            </div>
            <ProductPaletteProvider palette="ultralux">
              <OfferListEventsProvider tracking={tracking}>
                <OfferList
                  filters={offerListFilters}
                  pageSize={SEARCH_STREAMING_PAGE_SIZE}
                  onPageChange={setPage}
                  minRequiredOffers={minRequiredOffers}
                  interstitials={[{
                    id: 'noResults',
                    show: 'hasNoOffers',
                    position: 'start',
                    element: <VerticalSpacer gap={40}>
                      <Heading variant="heading4">{localizedVerticalContent.noResultsHeading}
                      </Heading>
                      <BodyText variant="large">
                        {localizedVerticalContent.noResultsBody}
                      </BodyText>
                      <TextButton kind="primary" to={vertical.verticalPageUrl}>
                        {localizedVerticalContent.viewMoreOptions}
                      </TextButton>
                    </VerticalSpacer>,
                  }]}
                />
              </OfferListEventsProvider>
            </ProductPaletteProvider>
          </SearchBurnProvider>
        </OffersSection>
      </LayoutContainer>
    </UltraluxVertical>
  </AnalyticsPageContext.Provider>
}

function mapStateToProps(state: App.State) {
  return {
    filters: getUrlOfferListFilters(state),
  }
}

export default connect(mapStateToProps)(UltraLuxVerticalPage)
